import { PLinkPure, useToastManager } from "@porsche-design-system/components-react"
import type {
	ConditionAndServiceView,
	InventoryInformationEditView,
	InventoryInformationView,
} from "@slfinrtl/ui-bff-routes"
import { getInventoryInformationEditViewRoute } from "@slfinrtl/ui-bff-routes"
import type { JSX } from "react"
import { useCallback, useRef, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useLocation } from "react-router"
import { commonMessages } from "src/common/messages/common.messages"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { AnimatedStage } from "src/components"
import { trackEvent, getVehicleDetailsAnalyticsPage } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff, useConfig } from "src/hooks"
import { useScrollToStage } from "src/screens/vehicleDetails/common/useScrollToStage/useScrollToStage"
import { useEditActionButton } from "src/screens/vehicleDetails/hooks/useEditActionButton"
import { useVehicleDetailsContextForStage } from "../../../context/VehicleDetailsContext"
import { ConditionAndServiceFormContainer } from "./ConditionAndServiceFormContainer"
import { ConditionAndService } from "./components/ConditionAndService"
import { conditionAndServiceContainerMessages } from "./conditionAndServiceContainer.messages"
import { conditionAndServiceStageId } from "./conditionAndServiceStageId.const"

interface ConditionAndServiceContainerProps {
	setView: (view: InventoryInformationView) => void
	view: ConditionAndServiceView
}

export const ConditionAndServiceContainer = withErrorHandler(({ setView, view }: ConditionAndServiceContainerProps) => {
	const { formatMessage } = useIntl()
	const { carPassEnabled } = useConfig()
	const { inventoryId, mode, notify, setEditStage } = useVehicleDetailsContextForStage("conditionService")
	const { addMessage } = useToastManager()
	const routes = useDealerAppRoutes()
	const { pathname } = useLocation()
	const analyticsPage = getVehicleDetailsAnalyticsPage(pathname, "overview")

	const [editView, setEditView] = useState<InventoryInformationEditView | undefined>()
	const [isEditViewLoading, setIsEditViewLoading] = useState(false)

	const ref = useRef<HTMLDivElement | null>(null)

	const fetchEditView = useBff(
		getInventoryInformationEditViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const handleEditClick = useCallback((): Promise<void> => {
		setIsEditViewLoading(true)
		return fetchEditView()
			.then((result) => {
				setEditView(result)
				setEditStage("conditionService")
			})
			.finally(() => setIsEditViewLoading(false))
	}, [fetchEditView, setEditStage])

	useScrollToStage({
		callbackFn: handleEditClick,
		editStage: "conditionService",
		ref,
	})

	const onExit = useCallback(() => {
		setEditStage(undefined)
	}, [setEditStage])

	const onSaved = useCallback(
		(inventoryInformationView: InventoryInformationView) => {
			setView(inventoryInformationView)
			onExit()
			addMessage({
				state: "success",
				text: formatMessage(commonMessages.saveSuccess),
			})
			notify({
				event: "inventoryInformationUpdated",
				payload: inventoryInformationView,
			})
			notify({
				event: "inventoryInformationViewChanged",
				payload: inventoryInformationView,
			})
		},
		[onExit, setView, addMessage, formatMessage, notify],
	)

	const pcssLink =
		view.vin && routes?.external ? (
			<PLinkPure
				key="pcss"
				href={routes.external.pcss.url(view.vin)}
				icon="external"
				underline
				alignLabel="start"
				target="_blank"
				onClick={() => trackEvent({ action: "show_vin_info_pcss", category: analyticsPage })}
			>
				<FormattedMessage {...conditionAndServiceContainerMessages.pcssLink} />
			</PLinkPure>
		) : undefined

	const onCarfaxReportLinkClick = useCallback(
		() => trackEvent({ action: "carfax_clicked", category: analyticsPage }),
		[analyticsPage],
	)

	const carfaxLink = view.carfaxReportUrl ? (
		<PLinkPure
			href={view.carfaxReportUrl}
			icon="external"
			underline
			alignLabel="start"
			target="_blank"
			onClick={onCarfaxReportLinkClick}
		>
			CARFAX
		</PLinkPure>
	) : undefined

	const actionButton = useEditActionButton("conditionService", view.historyEdit, {
		fetch: handleEditClick,
		isLoading: isEditViewLoading,
	})

	return (
		<AnimatedStage
			ref={ref}
			id={conditionAndServiceStageId}
			title={formatMessage(conditionAndServiceContainerMessages.title)}
			actionButtons={[pcssLink, carfaxLink, actionButton].filter(Boolean) as JSX.Element[]}
			data-testid="condition-and-service_container"
		>
			<ErrorNotificationInline />
			{mode !== "edit" || !editView ? (
				view && <ConditionAndService carPassEnabled={carPassEnabled} view={view} />
			) : (
				<ConditionAndServiceFormContainer
					inventoryId={inventoryId}
					editView={editView}
					onCancel={onExit}
					onSaved={onSaved}
				/>
			)}
		</AnimatedStage>
	)
})
